import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Customers from './Customers';
import EmailCampaigns from './EmailCampaigns';
import SendSchedule from './SendSchedule';
import DashboardOverview from './DashboardOverview';

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // Default to overview instead of customers
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'overview':
        return <DashboardOverview />;
      case 'customers':
        return <Customers />;
      case 'campaigns':
        return <EmailCampaigns />;
      case 'schedule':
        return <SendSchedule />;
      default:
        return <DashboardOverview />;
    }
  };

  const handleQuickSend = () => {
    setActiveTab('schedule');
  };
  
  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div className="md:hidden">
        <div className={`fixed inset-0 flex z-40 ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" 
               onClick={() => setIsMobileMenuOpen(false)}></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Mobile Sidebar Content */}
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <h1 className="text-xl font-bold text-indigo-600">MejlKampanj</h1>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {[
                  { name: 'Översikt', tab: 'overview', icon: 'chart-bar' },
                  { name: 'Kunder', tab: 'customers', icon: 'users' },
                  { name: 'E-postkampanjer', tab: 'campaigns', icon: 'mail' },
                  { name: 'Skicka/Schemalägg', tab: 'schedule', icon: 'calendar' }
                ].map((item) => (
                  <button
                    key={item.name}
                    onClick={() => setActiveTab(item.tab)}
                    className={`${
                      activeTab === item.tab
                        ? 'bg-indigo-100 text-indigo-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md w-full`}
                  >
                    {item.name}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center">
                <div>
                  <p className="text-sm font-medium text-gray-700">{currentUser.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-3 bg-red-100 text-red-800 py-1 px-2 rounded-md text-xs font-medium"
                >
                  Logga ut
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <h1 className="text-xl font-bold text-indigo-600">MejlKampanj</h1>
              </div>
              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {[
                  { name: 'Översikt', tab: 'overview', icon: 'chart-bar' },
                  { name: 'Kunder', tab: 'customers', icon: 'users' },
                  { name: 'E-postkampanjer', tab: 'campaigns', icon: 'mail' },
                  { name: 'Skicka/Schemalägg', tab: 'schedule', icon: 'calendar' }
                ].map((item) => (
                  <button
                    key={item.name}
                    onClick={() => setActiveTab(item.tab)}
                    className={`${
                      activeTab === item.tab
                        ? 'bg-indigo-100 text-indigo-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full`}
                  >
                    {item.name}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex-shrink-0 w-full group block">
                <div className="flex items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700 truncate">{currentUser.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top navigation bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <div className="flex-1 px-4 flex justify-between items-center">
            <div>
              {/* Mobile menu button */}
              <button
                className="md:hidden px-4 text-gray-500 focus:outline-none"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
            <div className="ml-4 flex items-center md:ml-6 space-x-4">
              {/* Quick Send button */}
              <button
                onClick={handleQuickSend}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Skicka kampanj
              </button>
              
              {/* User dropdown and logout button */}
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2 hidden sm:inline-block">
                  {currentUser.email}
                </span>
                <button
                  onClick={handleLogout}
                  className="bg-red-100 text-red-800 py-1 px-3 rounded-md text-sm font-medium hover:bg-red-200"
                >
                  Logga ut
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <h1 className="text-2xl font-semibold text-gray-900">{
                activeTab === 'overview' ? 'Översikt' :
                activeTab === 'customers' ? 'Kunder' : 
                activeTab === 'campaigns' ? 'E-postkampanjer' : 'Skicka & Schemalägg E-post'
              }</h1>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-4">
              {renderTabContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
